import JSZip from 'jszip';
import { downloadBlob, printPdfBlob } from '../../file_upload/download_blob';
export { downloadBlob };
/**
 * Fetches the compiled consent bundle ZIP and extracts the raw ICF PDF
 * (typically `icf.pdf` or the first `.pdf` file found in the archive).
 *
 * Returns an `application/pdf` Blob. JSZip's own `blob` type is octet-stream,
 * which Chrome prints as a binary dump instead of a document.
 */
export async function extractBlankPdfFromBundle(bundleUrl) {
    const response = await fetch(bundleUrl);
    if (!response.ok) {
        throw new Error('Unable to fetch consent bundle.');
    }
    const zip = await JSZip.loadAsync(await response.arrayBuffer());
    // Bundles are expected to contain exactly one ICF PDF; if a bundle has more than
    // one `.pdf` entry, the first match in the archive's directory order wins.
    const pdfEntry = zip.file('icf.pdf') ?? zip.file(/assets\/.*\.pdf$/i)[0] ?? zip.file(/\.pdf$/i)[0];
    if (!pdfEntry) {
        throw new Error('No PDF found in the consent bundle.');
    }
    const pdfBytes = await pdfEntry.async('uint8array');
    return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
}
/**
 * Obtains the unsigned print-to-sign PDF and either downloads it or opens the
 * browser print dialog.
 *
 * Both modes prefer the Studio bundle so Download and Print always serve the
 * same PDF. Print needs a fetchable blob for Chrome's PDF plugin, so it uses
 * the bundle only. Download falls back to the legacy `ec__pts_document` File
 * URL (navigate — these links are often cross-origin and cannot be fetched);
 * the details page withholds Print when there is no bundle (SSP-4679).
 *
 * A completed wet-signature document still uses this path: the digital stamp
 * builder refuses a blank template once status is `complete`.
 */
export async function copyBlankConsentPdf({ blankPdfUrl, bundleUrl, mode, fileName, }) {
    if (bundleUrl) {
        const blob = await extractBlankPdfFromBundle(bundleUrl);
        if (mode === 'download') {
            downloadBlob(blob, fileName);
        }
        else {
            printPdfBlob(blob);
        }
        return;
    }
    if (mode === 'download' && blankPdfUrl) {
        window.open(blankPdfUrl, '_blank', 'noopener,noreferrer');
        return;
    }
    throw new Error('No blank PDF source.');
}
